<template>
  <div class="style-cart-page container justify-content-center p-0 mt-3">
    <!-- Cart Item -->
    <div class="style-cart-item-margin">
      <div v-if="cartsOderBySale.length > 0">
        <div v-for="data in cartsOderBySale" :key="data.id" class="mb-4">
          <div class="style-product-container d-flex flex-row">
            <!-- need to change the sale price, APi does not return data.promotionPrice -->
            <expandable-list
              class="flex-grow-1"
              :border="data.product.promotion"
              @click="viewItemDetail(data)">
              <expandable-list-header
                v-if="data.product.promotion"
                :content="getPromotionHeader(data.product)"/>
              <expandable-list-item
                :imgUrl="data.product.image"
                :name="data.product.name"
                :processingTime="data.product.service ? data.product.service.formatProcessingTime : ''"
                :description="data.product.description"
                :originalPrice="data.product.originalPrice"
                :salePrice="data.product.salePrice"
                :clickable="false">
                <div class="d-flex flex-column justify-content-between m-2 position-relative">
                  <icon-button
                    :icon-class="'bi bi-x-circle'"
                    :iconStyle="{ fontSize: '18px' }"
                    @click.stop.prevent="removeFromCart(data.id)" />
                  <input
                    type="checkbox"
                    :value="data.id"
                    :disabled="data.product.showForEstore == 0 || data.product.published === 0"
                    class="style-checkbox form-check-input mx-auto"
                    v-model="selected"
                    @click.stop
                  />
                </div>
              </expandable-list-item>
            </expandable-list>
          </div>
          <div class="d-flex justify-content-end mt-2">
            <input-spinner
              v-if="data.product.showForEstore == 1 && data.product.published === 1"
              :id="data.id"
              :quantity="data.qty"
              :smallSpinner="true"
              @update:quantity=editQuantity
            />
            <span v-else>
              Unlisted
            </span>
          </div>
        </div>
      </div>
      <div v-else>
        <item-not-found message="Your cart is empty,  please add an item to check out." />
      </div>
    </div>

    <div class="style-payment-amount-margin">
      <!-- Display Total -->
      <total-row title="Total" :total="formatPriceSGD(totalAmount)" />

      <!-- Check Out Button -->
      <div class="mt-2">
        <default-button
          title="Check Out"
          :color=styleBtnColor
          :disabled="btnDisabled"
          @click="checkOutHandler"
        />
      </div>
    </div>

    <!-- Navigation Bar -->
    <div class="row justify-content-center fixed-bottom">
      <nav-bar />
    </div>
  </div>
</template>

<script>
import {
  defineAsyncComponent,
  markRaw
} from "vue";
import { mapActions, mapGetters } from "vuex";
import store from "@/store";
import DefaultButton from "@/components/button/DefaultButton.vue";
import ExpandableList from "@/components/ExpandableList.vue";
import ExpandableListHeader from "@/components/ExpandableListHeader.vue";
import ExpandableListItem from "@/components/ExpandableListItem.vue";
import ItemNotFound from "@/components/ItemNotFound.vue";
import IconButton from "@/components/button/IconButton.vue";
import NavBar from "@/components/NavBar.vue";
import TotalRow from "@/components/TotalRow.vue";
import InputSpinner from "@/components/input/InputSpinner.vue";
import { openModal, closeModal } from "@/store/modal";

import {
  formatPriceSGD,
  getFormattedNumber,
} from "@/common/helpers/PriceHelper.js";
import {
  getPromotionHeader,
  getItemLine,
} from "@/common/helpers/ProductHelper";

export default {
  name: "MyCart",
  components: {
    DefaultButton,
    ExpandableList,
    ExpandableListHeader,
    ExpandableListItem,
    ItemNotFound,
    IconButton,
    NavBar,
    TotalRow,
    InputSpinner,
  },
  data() {
    return {
      selected: [],
    };
  },
  methods: {
    async fetchCarts() {
      store.commit("showLoading", true);

      // always refetch whenever access my-cart page
      store.dispatch('getCarts');

      store.commit("showLoading", false);
    },
    checkOutHandler() {
      let selectedCartsData = [];
      selectedCartsData = this.selected.map(cartId => this.cartsOderBySale.find(cart => cart.id === cartId));

      sessionStorage.setItem("carts", JSON.stringify(selectedCartsData));

      this.$router.push('/my-cart/checkout');
    },
    async editQuantity(qty, id) {
      store.commit("showLoading", true);

      await store.dispatch('patchCart', {
        id,
        qty
      });

      store.commit("showLoading", false);
    },
    async removeFromCart(id) { // id is cart id
      store.commit("showLoading", true);

      // remove from selected(checked cart items) list if selected
      const selectedRemoveIndex = this.selected.indexOf(id);
      selectedRemoveIndex > -1 ? this.selected.splice(selectedRemoveIndex, 1) : null;

      // remove from cart (backend)
      await store.dispatch('deleteCart', {
        id,
      });

      store.commit("showLoading", false);
    },
    viewItemDetail(data) {
      const product = data.product;
      let itemLine = this.getItemLine(product.type, product);
      openModal(
        {
          id: "expandableListItemDetailModal",
        },
        markRaw(defineAsyncComponent(() => import("@/components/ExpandableListItemDetail.vue"))),
        {
          image: product.image,
          processingTime: itemLine.formatProcessingTime,
          name: product.name,
          description: product.description,
          originalPrice: product.originalPrice,
          salePrice: product.salePrice,
          section: product.productSection?.name || '-',
          category: product.productCategory?.name || '-',
          promotion: data.promotion,
        },
        {},
        [
          {
            text: 'Close',
            clickFn: () => {
              closeModal();
            }
          }
        ]
      );
    },
    getPromotionHeader,
    formatPriceSGD,
    getFormattedNumber,
    getItemLine,
  },
  computed: {
    btnDisabled() {
      // Enable button if at least one item had selected
      return this.selected.length <= 0;
    },
    styleBtnColor() {
      // temp solution
      // 1. not sure how to correct configure <default-button /> and unable to use SCSS variable on Vue method
      // 2. the following value based on /src/assets/scss/_color.scss $button-blue and $button-gray variable
      return this.selected.length > 0 ?
        '#579eff' : '#979797'
    },
    totalAmount() {
      return this.selected.reduce((accumulator, cartId) => {
        const cartData = this.cartsOderBySale.find((cart) => cart.id === cartId);
        return accumulator + (cartData?.product.salePrice * cartData.qty);
      }, 0);
    },
    ...mapGetters([
      "init",
      "cartsOderBySale",
    ]),
    ...mapActions(["getCarts"]),
  },
  created() {
    this.fetchCarts();
  },
};
</script>
<style lang="scss" scoped>
.style-cart-page {
  margin-bottom: 70px;
}
.style-checkbox {
  font-size: 20px;
}
.form-check-input:focus {
  box-shadow: none;
}
.style-product-container {
  border: 1px solid $border-light-grey;
  border-radius: 10px;
}
.style-cart-item-margin {
  width: 90%;
  margin: 0 auto 0 auto;
}
.style-payment-amount-margin {
  width: 95%;
  margin: 0 auto 0 auto;
}
</style>
